/**
 * Turnkey transaction submission type definitions.
 *
 * Relocated from frontend/src/lib/tx-engine/turnkey/types.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E). These are pure TypeScript
 * interface declarations — no runtime deps, no web/RN globals, no vendor imports.
 * Safe in src/auth/ core per .claude/rules/platform-agnostic.md.
 *
 * Type shapes mirror the minimal Turnkey surfaces the engine uses. The engine
 * supports the existing single-signer submission path, so its input excludes
 * any multi-signer transaction variants exposed by the vendor type.
 */

import type { SolanaTransaction } from '@turnkey/react-native-wallet-kit';

// ── Turnkey API request/response shapes ────────────────────────────────────

export interface TGetGasUsageBody {
  organizationId?: string;
}

export interface TGetGasUsageResponse {
  windowDurationMinutes: number;
  windowLimitUsd: string;
  usageUsd: string;
}

export interface TGetSendTransactionStatusBody {
  organizationId?: string;
  sendTransactionStatusId: string;
}

export interface V1SolanaSendTransactionStatus {
  signature?: string;
}

export interface V1SolanaFailureDetails {
  source?: string;
  rpcCode?: number;
  rpcMessage?: string;
  transactionErrorJson?: string;
  logs?: string[];
  unitsConsumed?: string;
  innerInstructionsJson?: string;
}

export interface V1TxError {
  message?: string;
  solana?: V1SolanaFailureDetails;
}

export interface TGetSendTransactionStatusResponse {
  txStatus: string;
  solana?: V1SolanaSendTransactionStatus;
  txError?: string;
  error?: V1TxError;
}

export interface TGetActivityBody {
  organizationId?: string;
  activityId: string;
}

export interface V1Activity {
  id?: string;
  status?: string;
  // ALLOW-UNKNOWN: Turnkey activity result is a free-form payload (operation-specific)
  result?: unknown;
}

export interface TGetActivityResponse {
  activity: V1Activity;
}

/** Sanitized Turnkey lifecycle state captured by the platform hook. */
export interface TurnkeyRuntimeSnapshot {
  clientState: string;
  authState: string;
  hasHttpClient: boolean;
  hasSession: boolean;
}

export type SolSendTransactionInput = Extract<SolanaTransaction, { signWith: string }>;

/**
 * Capability bundle injected from `useTurnkeySubmissionDeps()`. Consumed
 * only by the embedded/imported branches; external (`relay-primary`)
 * submissions never invoke any field on it (router selects path before
 * any TurnkeyDeps field is read).
 */
export interface TurnkeyDeps {
  /** Lifecycle diagnostics only. Never contains session material or credentials. */
  runtimeSnapshot: TurnkeyRuntimeSnapshot;
  /** Returns the `sendTransactionStatusId` for poll. Throws on quota/rate-limit/etc. */
  solSendTransaction: (params: { transaction: SolSendTransactionInput }) => Promise<string>;
  /** Single status query — used by the custom poller (FR-S02..S05). */
  getSendTransactionStatus: (
    body: TGetSendTransactionStatusBody,
  ) => Promise<TGetSendTransactionStatusResponse>;
  /** Pre-flight cap check (FR-003). */
  getGasUsage: (body: TGetGasUsageBody) => Promise<TGetGasUsageResponse>;
  /** Activity poller — used to wait out `ACTIVITY_STATUS_PENDING` (FR-S05a). */
  getActivity: (body: TGetActivityBody) => Promise<TGetActivityResponse>;
}

/**
 * Inert TurnkeyDeps used when the user is on an external (connected)
 * wallet and Turnkey context is unavailable. Every method rejects with
 * a marker error. Per data-model.md and T118, the engine never invokes
 * these on the external branch — but the type system requires them.
 */
export function createInertTurnkeyDeps(
  runtimeSnapshot: TurnkeyRuntimeSnapshot = {
    clientState: 'unavailable',
    authState: 'unauthenticated',
    hasHttpClient: false,
    hasSession: false,
  },
): TurnkeyDeps {
  const reject = async <T>(): Promise<T> => {
    throw new Error('TurnkeyDeps invoked from external-wallet path');
  };
  return {
    runtimeSnapshot,
    solSendTransaction: reject,
    getSendTransactionStatus: reject,
    getGasUsage: reject,
    getActivity: reject,
  };
}
